<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useField } from 'vee-validate'
import { getLocalizedText } from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import type { FormField } from '../types/formTypeStructure'
import { AlertCircle } from "lucide-vue-next";

interface BoolFormField extends FormField {
  fieldType: "bool";
}

interface BoolFormFieldProps {
  field: BoolFormField;
  name?: string;
  currentLang?: string;
  defaultValue?: boolean;
  className?: string;
}

const props = defineProps<BoolFormFieldProps>()

const fieldName = computed(() => props.name ?? props.field.fieldName.toString())
const currentLang = computed(() => props.currentLang ?? 'fr')
const label = computed(() => getLocalizedText(props.field.label, currentLang.value))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const { value, handleChange, errorMessage, meta, setTouched } = useField<boolean>(
  fieldName.value,
  undefined,
  {
    initialValue: props.defaultValue ?? false,
  }
)

function onCheckboxChange(event: Event) {
  const target = event.target as HTMLInputElement
  handleChange(target.checked)
  setTouched(true)
}
</script>

<template>
  <div class="bool-field-container">
    <!-- Label dupliqué au-dessus du checkbox -->
    <div class="bool-field-label-container">
      <span class="bool-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper
      :field="field"
      :current-lang="currentLang"
      :show-label="false"
    >
      <label class="flex items-center gap-2 cursor-pointer" :class="className">
        <input
          type="checkbox"
          class="accent-[#04406E] focus:ring-2 focus:ring-[#04406E] border-[#04406E] w-5 h-5"
          :checked="value"
          @change="onCheckboxChange"
        />
        <span class="text-[#04406E] font-medium">
          {{ label }}
          <span v-if="isRequired" class="text-red-500 ml-1">*</span>
        </span>
      </label>

      <p v-if="meta.touched && errorMessage" class="text-sm text-red-500 mt-1">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.bool-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.bool-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.bool-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>
